"use client";

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * Analytics consent, stored per browser.
 *
 * `null` means undecided — the banner is showing and nothing has loaded. The
 * value starts as undecided on the server and is only read from localStorage
 * after mount, because the server cannot know the choice and rendering one
 * would cause a hydration mismatch.
 *
 * Deliberately opt-in: analytics stay off until someone actively accepts.
 * Under GDPR consent must be a positive act, so a pre-ticked box or a
 * dismiss-to-accept banner would not count.
 */
export type Consent = "granted" | "denied" | null;

const KEY = "trialion.analytics-consent";

type Ctx = {
  consent: Consent;
  /** False until the stored value has been read, so nothing flashes on load. */
  ready: boolean;
  accept: () => void;
  decline: () => void;
  /** Reopens the banner so a choice can be changed. */
  reset: () => void;
};

const ConsentContext = createContext<Ctx | null>(null);

function read(): Consent {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    // Private browsing and blocked site data both throw here. Treat an
    // unreadable store as undecided rather than as consent.
    return null;
  }
}

function write(v: Consent) {
  try {
    if (v === null) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, v);
  } catch {
    /* nothing we can do; the choice simply will not persist */
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<Consent>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setConsent(read());
    setReady(true);
  }, []);

  const set = useCallback((v: Consent) => {
    setConsent(v);
    write(v);
  }, []);

  return (
    <ConsentContext.Provider
      value={{
        consent,
        ready,
        accept: () => set("granted"),
        decline: () => set("denied"),
        reset: () => set(null),
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(): Ctx {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside <ConsentProvider>");
  return ctx;
}
